import { createSelector } from 'reselect';
import { selectActiveConsumersSinceLastMensiversary } from './participantSelectors';
import {
    doesAlgoNeedParams,
    doesAlgoSupportTemporaryUpload,
    isAllocationAlgorithmCustomized,
} from '../../../../app-config';
import { selectSortedByEnedisIdOperations } from './operationSelectors';
import moment from 'moment';

const selectAllocationEnergy = (state) => state.energyAllocation;

const selectNewData = createSelector(
    selectAllocationEnergy,
    (energyAllocation) => energyAllocation.newData
);
const selectPersistedData = createSelector(
    selectAllocationEnergy,
    (energyAllocation) => energyAllocation.persistedData
);

export const selectNewAllocationAlgorithmType = createSelector(
    selectNewData,
    (newData) => newData.allocationAlgorithmType
);

export const selectNewParticipantsParameters = createSelector(
    selectNewData,
    (newData) => newData.participantsParameters
);

export const selectPersistedAllocationAlgorithmConfig = createSelector(
    selectPersistedData,
    (data) => data.allocationAlgorithmConfig
);

export const selectOperationAllocationAlgorithmId = createSelector(
    selectPersistedAllocationAlgorithmConfig,
    (config) => config?.id
);

export const selectOperationAllocationAlgorithmType = createSelector(
    selectPersistedAllocationAlgorithmConfig,
    (config) => config?.type
);

export const selectPersistedParticipantsParameters = createSelector(
    selectPersistedData,
    (newData) => newData.participantsParameters
);
export const selectParamsSum = createSelector(
    selectNewParticipantsParameters,
    (params) =>
        Object.values(params).reduce((acc, val) => {
            if (val === '' || isNaN(val)) {
                return acc;
            }
            return acc + Number(val);
        }, 0)
);

const selectIsNewAllocationValid = createSelector(
    selectNewAllocationAlgorithmType,
    selectNewParticipantsParameters,
    selectActiveConsumersSinceLastMensiversary,
    (newAlgorithmType, params, consumers) => {
        if (doesAlgoNeedParams(newAlgorithmType)) {
            return consumers.every(
                ({ id }) => params[id] !== '' && !isNaN(params[id])
            );
        }
        return !!newAlgorithmType;
    }
);

export const selectHasAllocationChanged = createSelector(
    selectPersistedAllocationAlgorithmConfig,
    selectPersistedParticipantsParameters,
    selectNewAllocationAlgorithmType,
    selectNewParticipantsParameters,
    (persistedConfig, persistedParams, newAlgorithmType, newParams) => {
        const hasConfigChanged = persistedConfig?.type !== newAlgorithmType;
        const hasParamsLengthChanged =
            Object.keys(persistedParams).length !==
            Object.keys(newParams).length;

        const hasParamsValuesChanged =
            JSON.stringify(persistedParams) !== JSON.stringify(newParams);
        return (
            hasConfigChanged || hasParamsLengthChanged || hasParamsValuesChanged
        );
    }
);

export const selectCanSubmitNewAllocation = createSelector(
    selectIsNewAllocationValid,
    selectHasAllocationChanged,
    (isValid, hasChanged) => {
        return isValid && hasChanged;
    }
);

export const selectHasAllocationTypeChanged = createSelector(
    selectPersistedAllocationAlgorithmConfig,
    selectNewAllocationAlgorithmType,
    (persistedConfig, newAlgorithmType) => {
        const hasConfigChanged = persistedConfig?.type !== newAlgorithmType;
        if (!hasConfigChanged) return false;
        if (
            isAllocationAlgorithmCustomized(persistedConfig?.type) &&
            isAllocationAlgorithmCustomized(newAlgorithmType)
        )
            return false;
        return true;
    }
);

export const selectPersistedOperationSeasonality = createSelector(
    selectPersistedData,
    (data) => data.operationSeasonality
);

export const selectTemporaryUploadRuns = (state) =>
    state.distributionTracking.temporaryUploadRuns;

export const selectTemporaryUploadOperations = createSelector(
    selectSortedByEnedisIdOperations,
    (operations) => {
        const currentMonthStart = moment().startOf('month');
        return operations.filter(
            (op) =>
                op.id !== 31 &&
                doesAlgoSupportTemporaryUpload(op.allocationType) &&
                moment(op.anniversaryDate).isBefore(currentMonthStart)
        );
    }
);

export const selectDistributionRuns = createSelector(
    selectTemporaryUploadRuns,
    selectTemporaryUploadOperations,
    (temporaryUploadRuns, operations) => {
        const runs = [];
        const operationsWithRuns = new Set();
        const seenOperations = new Set();
        temporaryUploadRuns
            ?.sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
            )
            .forEach((run) => {
                const operation = operations.find(
                    (op) => op.id === run.operationId
                );
                if (seenOperations.has(run.operationId)) return;
                seenOperations.add(run.operationId);
                if (operation) {
                    operationsWithRuns.add(operation.id);
                    runs.push({
                        ...run,
                        enedisId: operation.enedisId,
                        operationId: operation.id,
                        name: operation.name,
                        allocationType: operation.allocationType,
                        exec: run.createdAt
                            ? new Date(run.createdAt).toLocaleString('fr-FR')
                            : '-',
                        steps: run.steps || [],
                    });
                }
            });
        const operationsWithoutRuns = operations.filter(
            (op) => !operationsWithRuns.has(op.id)
        );
        operationsWithoutRuns.forEach((op) => {
            runs.push({
                id: null,
                enedisId: op.enedisId,
                operationId: op.id,
                name: op.name,
                allocationType: op.allocationType,
                status: 'READY',
                exec: '-',
                steps: [],
            });
        });

        const statusOrder = [
            'READY',
            'FAILURE',
            'IN_PROGRESS',
            'PENDING',
            'SUCCESS',
        ];

        return runs.sort((a, b) => {
            const orderA = statusOrder.indexOf(a.status);
            const orderB = statusOrder.indexOf(b.status);
            return orderA - orderB;
        });
    }
);

export const selectDistributionKpis = createSelector(
    selectDistributionRuns,
    (runs) => {
        return {
            ready: runs.filter((r) => r.status === 'READY').length,
            pending: runs.filter((r) => r.status === 'PENDING').length,
            inProgress: runs.filter((r) => r.status === 'IN_PROGRESS').length,
            success: runs.filter((r) => r.status === 'SUCCESS').length,
            error: runs.filter((r) => r.status === 'FAILURE').length,
        };
    }
);
export const selectFilteredDistributionRuns = createSelector(
    [selectDistributionRuns, (state, filters) => filters],
    (runs, filters) => {
        let filteredRuns = runs;
        if (filters.operationId) {
            filteredRuns = filteredRuns.filter(
                (run) => run.operationId === filters.operationId
            );
        }
        if (filters.status) {
            filteredRuns = filteredRuns.filter(
                (run) => run.status === filters.status
            );
        }
        if (filters.allocationType) {
            filteredRuns = filteredRuns.filter(
                (run) => run.allocationType === filters.allocationType
            );
        }
        return filteredRuns;
    }
);
